import {
  CartItemTypes,
  ICartItemType,
  ICartItem_Product,
} from "@/api/AppApi/services/cart/interfaces/cart-interfaces";
import {
  ICommonProductDetails,
  IProductAddOn,
} from "@/api/AppApi/services/product/interfaces/api-product-details-interface";
import {
  IAddOnWithQuantity,
  IAddons,
  IGroupedBundleItems,
  ISelectedBundleItems,
} from "./interfaces";
import { addonsInitValues } from "@/app/components/common-addons-builder/addon-functions";
import {
  groupBundleItemsByBundleItemId,
  ungroupBundleItems,
} from "@/app/components/pages-components/product-details-page/bundle/functions";
import { IBundleItem } from "@/api/AppApi/services/product/interfaces/product-bundle-types";

export type getProductFromCartReturnType = {
  quantity: number;
  selectedAddons: any;
  note: string;
  addedToCart: boolean;
  other_inputs: object;
  unit_id?: number;
  price?: number;
  bundle_items: ISelectedBundleItems[];
};

export const getProductFromCart = (
  cartItems: ICartItemType[],
  product: ICommonProductDetails,
  show_all_product_units: boolean,
  cartItemID?: number,
): getProductFromCartReturnType => {
  const initAddOns = (addons_builder: any[]) => {
    if (!addons_builder || addons_builder?.length === 0) return {};
    return addons_builder?.reduce(
      (acc, addOn) => ({ ...acc, [addOn.configurations.key]: [] }),
      {},
    );
  };
  // if (!cartItems) return null;
  const index = (cartItems ?? [])?.findIndex(
    (item) => item.inner_id === product?.id && item.id === cartItemID,
  );
  if (index < 0)
    return {
      quantity: product.min_quantity_in_cart ?? 1,
      selectedAddons: initAddOns(product?.addons_builder),
      note: "",
      addedToCart: false,
      other_inputs: {},
      unit_id: show_all_product_units ? product.units[0]?.id : product.unit_id,
      bundle_items: [],
    };
  if (cartItems[index].type === CartItemTypes.product) {
    return {
      quantity: cartItems[index].quantity,
      selectedAddons: addonsInitValues(cartItems[index].addons),
      note: cartItems[index].note,
      addedToCart: true,
      other_inputs: { ...cartItems[index]?.others?.data_fields },
      unit_id:
        (cartItems[index] as ICartItem_Product).others.unit_id ??
        product.unit_id,
      price: cartItems[index].price,
      bundle_items: ungroupBundleItems(
        (cartItems[index] as ICartItem_Product).bundle_items ?? [],
      ),
    };
  }
  return {
    quantity: cartItems[index].quantity,
    selectedAddons: addonsInitValues(cartItems[index].addons),
    note: cartItems[index].note,
    addedToCart: true,
    other_inputs: { ...cartItems[index]?.others?.data_fields },
    price: cartItems[index].price,
    bundle_items: [],
  };
};

export const isRequiredAddonsNotChoosed = (
  addons: IProductAddOn[],
  selectedAddons: IAddons,
) => {
  for (let i = 0; i < addons?.length; i++) {
    const selectedAddon = selectedAddons[addons[i]?.configurations.key];
    const selectedAddonQuantity =
      selectedAddon?.reduce((prev, current) => prev + current.quantity, 0) ?? 0;
    const selectedAddonMinQuantity = addons[i].configurations.min_quantity ?? 0;
    if (
      addons[i].configurations.is_required &&
      (!selectedAddon ||
        selectedAddon.length === 0 ||
        selectedAddonQuantity < selectedAddonMinQuantity)
    )
      return true;
  }
  return false;
};

export const getSelectedAddOnsPrice = (
  allAddons: IProductAddOn[],
  selectedAddons: IAddOnWithQuantity[],
) => {
  const flatAddons = allAddons.map((addon) => addon.data).flat();
  const flatcurrentAddons = selectedAddons.flat();
  let currentAddonsPrice = 0;
  for (let i = 0; i < flatcurrentAddons.length; i++)
    currentAddonsPrice +=
      (flatAddons.find((addon) => addon.id === flatcurrentAddons[i].id)
        ?.price ?? 0) * flatcurrentAddons[i].quantity;
  return currentAddonsPrice;
};

export function compareAddOnsArrays(
  arr1: IAddOnWithQuantity[],
  arr2: IAddOnWithQuantity[],
  withQuantities?: boolean,
): boolean {
  if (arr1.length !== arr2.length) {
    return false;
  }
  if (!arr1.length && !arr2.length) return true;

  // Sort both arrays by `id` (and optionally by `quantity` if needed)
  const sortedArr1 = arr1.slice().sort((a, b) => a.id - b.id);
  const sortedArr2 = arr2.slice().sort((a, b) => a.id - b.id);

  // Compare each object in the sorted arrays
  for (let i = 0; i < sortedArr1.length; i++) {
    if (
      sortedArr1[i].id !== sortedArr2[i].id ||
      (withQuantities && sortedArr1[i].quantity !== sortedArr2[i].quantity)
    ) {
      return false;
    }
  }

  // If all objects match, return true
  return true;
}

export const findCartItemByAddOnsAndBundle = (
  cartItems: any[], // Replace 'any' with your actual cart item type
  currentProd: { id: number } | null | undefined,
  addOns: any[], // Replace 'any' with your actual addon type
  selectedBundleItems: ISelectedBundleItems[],
  compareAddOnsArrays: (addons1: any[], addons2: any[]) => boolean,
  compareBundleItems: (items1: IGroupedBundleItems[], items2: any[]) => boolean,
  CartItemTypes: { product: string }, // Replace with your actual CartItemTypes type
) => {
  return (cartItems ?? [])?.find((item) => {
    // Basic conditions
    const basicMatch =
      item.inner_id === currentProd?.id &&
      compareAddOnsArrays(item.addons, addOns.flat());

    // If it's a product type, also check bundle items
    if (item.type === CartItemTypes.product) {
      const currentBundleItems =
        groupBundleItemsByBundleItemId(selectedBundleItems);
      const itemBundleItems = item.bundle_items || [];

      return (
        basicMatch && compareBundleItems(currentBundleItems, itemBundleItems)
      );
    }

    return basicMatch;
  });
};

export const getBundleMinQuantityOnHand = (
  product: ICommonProductDetails,
  selectedBundleItems: ISelectedBundleItems[],
) => {
  const bundleItemsAsObject: Record<number, IBundleItem> = (
    product.bundle_items ?? []
  )?.reduce((prev, current) => ({ ...prev, [current.id]: { ...current } }), {});

  const minQuantityOnHandArray: number[] = [];
  selectedBundleItems.forEach((selectedBundleItem) => {
    if (selectedBundleItem.is_configurable) {
      minQuantityOnHandArray.push(
        bundleItemsAsObject[
          selectedBundleItem.bundle_item_id
        ].product.children.find(
          (child) => child.id === selectedBundleItem.child,
        )?.quantity_on_hand ?? 0,
      );
    } else {
      minQuantityOnHandArray.push(
        bundleItemsAsObject[selectedBundleItem.bundle_item_id].product
          .quantity_on_hand,
      );
    }
  });
  const minQuantityOnHand = Math.min(...minQuantityOnHandArray);

  console.log({
    minQuantityOnHandArray,
    minQuantityOnHand,
    bundleItemsAsObject,
  });
  return minQuantityOnHand;
};

export const isBundleOutOfStock = (
  product: ICommonProductDetails,
  selectedBundleItems: ISelectedBundleItems[],
  currentProductQuantityInCart: number,
) => {
  const bundleItemsAsObject: Record<number, IBundleItem> = (
    product.bundle_items ?? []
  )?.reduce((prev, current) => ({ ...prev, [current.id]: { ...current } }), {});

  for (const selectedBundleItem of selectedBundleItems) {
    const bundleItem = bundleItemsAsObject[selectedBundleItem.bundle_item_id];
    if (!bundleItem) continue;


    if (selectedBundleItem.is_configurable) {
      // Required quantity is 1 for each child
      const childProduct = bundleItem.product.children.find(
        (child) => child.id === selectedBundleItem.child,
      );
      if (!childProduct) continue;

      const availableQuantity =
        childProduct.quantity_on_hand -
        currentProductQuantityInCart * bundleItem.quantity;

      if (availableQuantity < 1) {
        return true;
      }
    } else {
      // Required quantity is the bundle item quantity
      const availableQuantity =
        bundleItem.product.quantity_on_hand -
        currentProductQuantityInCart * bundleItem.quantity;

      if (availableQuantity < bundleItem.quantity) {
        return true;
      }
    }
  }

  return false;
};
